import { SparkBuilderStep } from '../../spark-ads.types';
import { MIN_DAILY_BUDGET, MIN_LIFETIME_BUDGET } from './spark-ads-builder.config';

/** The state each step's validation reads. */
export interface BuilderValidationInput {
  campaignName: string | undefined;
  objectiveApiValue: string | undefined;
  advertiserId: string | undefined;

  adGroupName: string | undefined;
  locationIds: readonly string[];
  budget: number;
  budgetIsDaily: boolean;
  startDate: string | undefined;
  endDate: string | undefined;

  selectedVideoId: string | undefined;
  /** Whether the chosen post carries a live Spark authorisation. */
  selectedVideoAuthorised: boolean;
  callToAction: string | undefined;
  landingPageUrl: string | undefined;
}

/**
 * The reason a step cannot continue, phrased for the user.
 *
 * `null` means the step is ready. The design is explicit that a blocked Continue must
 * always say why — never a bare disabled button.
 */
export type BlockedReason = string | null;

export function blockedReasonForStep(
  step: SparkBuilderStep,
  input: BuilderValidationInput,
): BlockedReason {
  switch (step) {
    case 1:
      return campaignBlockedReason(input);
    case 2:
      return adGroupBlockedReason(input);
    case 3:
      return sparkAdBlockedReason(input);
    default:
      return null;
  }
}

function campaignBlockedReason(input: BuilderValidationInput): BlockedReason {
  if (!input.campaignName?.trim()) {
    return 'Give the campaign a name so you can find it in reporting later.';
  }
  if (!input.objectiveApiValue) {
    return 'Pick one goal — TikTok optimises everything underneath it around that goal.';
  }
  if (!input.advertiserId) {
    return 'Choose which TikTok ad account this campaign spends from.';
  }
  return null;
}

function adGroupBlockedReason(input: BuilderValidationInput): BlockedReason {
  if (!input.adGroupName?.trim()) {
    return 'Name the ad group — it is how you will tell your audiences apart.';
  }
  if (input.locationIds.length === 0) {
    return 'Add at least one market so TikTok knows where to deliver.';
  }

  const minimum = input.budgetIsDaily ? MIN_DAILY_BUDGET : MIN_LIFETIME_BUDGET;
  if (!Number.isFinite(input.budget) || input.budget < minimum) {
    return input.budgetIsDaily
      ? `TikTok needs at least ${minimum} a day to deliver an ad group.`
      : `A lifetime budget needs to be at least ${minimum}.`;
  }

  const scheduleReason = scheduleBlockedReason(input.startDate, input.endDate);
  if (scheduleReason) {
    return scheduleReason;
  }

  return null;
}

function scheduleBlockedReason(
  startDate: string | undefined,
  endDate: string | undefined,
): BlockedReason {
  if (!startDate) {
    return 'Set a start date and time.';
  }
  if (!endDate) {
    return 'Set an end date and time.';
  }

  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return 'The schedule dates are not valid.';
  }
  if (end <= start) {
    return 'The end date needs to be after the start date.';
  }
  return null;
}

function sparkAdBlockedReason(input: BuilderValidationInput): BlockedReason {
  if (!input.selectedVideoId) {
    return 'Choose the creator post you are amplifying.';
  }
  if (!input.selectedVideoAuthorised) {
    return 'This post needs the creator’s Spark authorisation before it can carry budget.';
  }
  if (!input.callToAction) {
    return 'Pick the button people tap on the ad.';
  }
  if (!input.landingPageUrl?.trim()) {
    return 'Add the page the ad sends people to.';
  }
  if (!isLikelyUrl(input.landingPageUrl)) {
    return 'That destination does not look like a valid web address.';
  }
  return null;
}

/**
 * Accepts input with or without a scheme, since people paste `shop.com/ss26` as often
 * as a full URL. The scheme is added before submitting.
 */
export function isLikelyUrl(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed || /\s/.test(trimmed)) {
    return false;
  }

  try {
    const url = new URL(/^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`);
    return url.hostname.includes('.') && !url.hostname.startsWith('.') && !url.hostname.endsWith('.');
  } catch {
    return false;
  }
}

/** Normalises a destination for submission by adding `https://` when missing. */
export function normaliseUrl(value: string): string {
  const trimmed = value.trim();
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

/** Readiness note shown beside Continue when the step is good to go. */
export function readyNoteForStep(step: SparkBuilderStep): string {
  switch (step) {
    case 1:
      return 'Goal set — you’re good to go.';
    case 2:
      return 'Audience and budget look healthy.';
    case 3:
      return 'One authorised post selected.';
    default:
      return '';
  }
}
